import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Sunrise, BoxIcon } from "lucide-react";
import { HERO_BG, CLOSING_BG, chapters } from "../data/horizon";
import { INVASION_BG } from "../data/invasions";
import { visibleCategories } from "../utils/guideIndex";
type Section = {
  to: string;
  image: string;
  icon: BoxIcon;
  eyebrow: string;
  title: string;
  description: string;
  tags: string[];
  cta: string;
};
const sections: Section[] = [{
  to: '/guia',
  image: INVASION_BG,
  icon: BookOpen,
  eyebrow: 'Cómo funciona hoy',
  title: 'Guía',
  description: 'Mecánicas, ítems, mapas, monstruos e invasiones del servidor, con dónde conseguir cada cosa.',
  tags: visibleCategories.slice(0, 3).map(c => c.title),
  cta: 'Abrir la guía'
}, {
  to: '/proyeccion',
  image: CLOSING_BG,
  icon: Sunrise,
  eyebrow: 'Hacia dónde vamos',
  title: 'Proyección',
  description: `La hoja de ruta del servidor en ${chapters.length} capítulos: progreso, mazmorras, prestigio, economía y comunidad.`,
  tags: ['Progreso', 'Economía', 'Comunidad'],
  cta: 'Ver la proyección'
}];
export function Home() {
  useEffect(() => {
    document.title = 'MU Vanguard · Guía y Proyección';
  }, []);
  return <main className="relative overflow-hidden" data-id="element-2569" data-mp="2qx0tgbfxlcdv">
      <div className="absolute inset-x-0 top-0 h-[520px] bg-cover bg-center opacity-30" style={{
      backgroundImage: `url("${HERO_BG}")`
    }} aria-hidden="true" data-id="element-2570" data-mp="2mxa7qm59hmg7" />
      <div className="absolute inset-x-0 top-0 h-[520px] bg-[#050b14]/60" aria-hidden="true" data-id="element-2571" data-mp="2i00a4j7py4ha" />
      <div className="absolute inset-x-0 top-[320px] bottom-0 bg-[#050b14]" aria-hidden="true" data-id="element-2572" data-mp="2d5w2ryujjufo" />

      <div className="relative mx-auto max-w-6xl px-4 pb-16 pt-24 sm:px-6 md:pb-24 md:pt-44 lg:px-8" data-id="element-2573" data-mp="288dpxxdekv2e">
        <header className="mx-auto max-w-3xl text-center" data-id="element-2574" data-mp="236y4w5b86ok0">
          <p className="font-cinzel text-xs font-semibold text-[#D4AF37] md:text-base" data-id="element-2575" data-mp="1z6e852ukdmye">Centro de información</p>
          <h1 className="mt-2 font-cinzel text-[1.75rem] font-black leading-[1.1] tracking-tight text-white sm:text-5xl md:mt-3 md:text-6xl" data-id="element-2576" data-mp="1u9ns2firercz">
            Todo sobre <span className="text-[#D4AF37]" data-id="element-2577" data-mp="1omc3zbw6zcty">MU Vanguard</span>
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-gray-300 md:mt-4 md:text-lg" data-id="element-2578" data-mp="1ok7tuc7p9hl8">Cómo funciona el servidor hoy y hacia dónde vamos. Elige por dónde empezar.</p>
        </header>

        <div className="mt-7 grid grid-cols-2 gap-3 md:mt-14 md:gap-6" data-id="element-2579" data-mp="1fnqjhb75tnqj">
          {sections.map(s => <SectionCard key={s.to} s={s} data-id="element-2580" data-mp="23dbdcp4uq5el" />)}
        </div>
      </div>
    </main>;
}
function SectionCard({
  s
}: {
  s: Section;
}) {
  const Icon = s.icon;
  return <Link to={s.to} className="group flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0A1628] transition-colors duration-200 hover:border-[#D4AF37]/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37] md:rounded-3xl" data-id="element-2581" data-mp="1tkdqk6sttua6">
      <div className="relative aspect-[4/3] w-full overflow-hidden md:aspect-[16/9]" data-id="element-2582" data-mp="2r0j5e5d9rhyo">
        <img src={s.image} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]" data-id="element-2583" data-mp="2r2m2f9cuabcw" />
        <div className="absolute inset-0 bg-[#050b14]/35" aria-hidden="true" data-id="element-2584" data-mp="alxuctwunuxi" />
        <span className="absolute left-2.5 top-2.5 flex h-8 w-8 items-center justify-center rounded-xl bg-[#D4AF37] text-[#050b14] md:left-5 md:top-5 md:h-11 md:w-11 md:rounded-2xl" data-id="element-2585" data-mp="oedwlshxfoxd">
          <Icon className="h-4 w-4 md:h-5 md:w-5" aria-hidden="true" data-id="element-2586" data-mp="3sru6lgposygl" />
        </span>
      </div>

      <div className="flex min-w-0 flex-1 flex-col p-3 sm:p-5 md:p-7" data-id="element-2587" data-mp="3ntslyrcm4r30">
        <p className="font-cinzel text-[11px] font-bold text-[#D4AF37] md:text-sm" data-id="element-2588" data-mp="3nvlwihqcjvga">{s.eyebrow}</p>
        <h2 className="mt-0.5 font-cinzel text-xl font-black leading-tight text-white sm:text-2xl md:mt-1.5 md:text-4xl" data-id="element-2589" data-mp="5l9xvspuqlvw">{s.title}</h2>
        <p className="mt-1.5 line-clamp-3 text-xs leading-snug text-gray-300 sm:text-sm md:mt-3 md:line-clamp-none md:text-base md:leading-relaxed" data-id="element-2590" data-mp="1h207zmxebxus">{s.description}</p>
        <ul className="mt-4 hidden flex-wrap gap-2 md:flex" data-id="element-2591" data-mp="1c62udodip5lp">
          {s.tags.map(t => <li key={t} className="rounded-full bg-white/[0.07] px-3 py-1 text-xs font-semibold text-gray-300" data-id="element-2592" data-mp="27fpw4h58ilc7">{t}</li>)}
        </ul>
        <div className="mt-auto pt-3 md:pt-6" data-id="element-2593" data-mp="2hcccotqj2q8f">
          <span className="flex h-9 w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-3 font-cinzel text-xs font-bold text-[#050b14] whitespace-nowrap transition-colors duration-150 group-hover:bg-[#F3E5AB] md:inline-flex md:h-12 md:w-auto md:px-7 md:text-sm" data-id="element-2594" data-mp="2hb9jgf5qgbpc">
            {s.cta}
            <ArrowRight className="hidden h-4 w-4 transition-transform duration-200 ease-out group-hover:translate-x-1 sm:block" aria-hidden="true" data-id="element-2595" data-mp="2va9atukmr4p7" />
          </span>
        </div>
      </div>
    </Link>;
}